import { useEffect, useMemo, useState } from 'react';
import {
  Table,
  Button,
  Space,
  Modal,
  Input,
  Empty,
  Tooltip,
  Tag
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  ReloadOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  GiftOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { internalAccounts } from '../../api/internalAdminEndpoints';
import { useAppNotifications } from '../../utils/notify';
import LoadingBlock from '../../components/LoadingBlock';
import PageHeader from '../../components/PageHeader';
import { formatDateTime } from '../../utils/format';
import useInternalAdminAuth from '../../contexts/InternalAdminAuthContext';
import StatCard from '../../components/internalAdmin/StatCard';
import {
  CompanyCell,
  ContactCell,
  PlanCell,
  PaymentCell,
  MoneyText,
  paiseToINR
} from '../../components/internalAdmin/AccountCells';

// Stage 5N-2C: pending-accounts page.
//
// Layout:
//   1. Page header with refresh + count chip
//   2. Four summary stat cards
//   3. AntD Table with fixed column widths so nothing wraps badly
//      and Actions column pinned right
//
// Column widths are explicit — the table sets a min-width via the
// `scroll.x` so AntD will horizontal-scroll inside the table only
// if the viewport is narrower than ~1200px (Actions still visible).

const { TextArea } = Input;

export default function PendingAccountsPage() {
  const { hasPermission } = useInternalAdminAuth();
  const { notifySuccess, notifyError } = useAppNotifications();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ items: [], total: 0 });

  const [approveTarget, setApproveTarget] = useState(null);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState(false);

  const canApprove = hasPermission('accounts.approve');
  const canReject = hasPermission('accounts.reject');

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const resp = await internalAccounts.listPending({ limit: 50 });
      setData({
        items: Array.isArray(resp.items) ? resp.items : [],
        total: typeof resp.total === 'number' ? resp.total : 0
      });
    } catch (err) {
      setError(err);
      notifyError(err, { title: 'Could not load pending accounts' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Aggregate stats — computed client-side on the loaded page so the
  // counts stay in sync with whatever the operator sees in the table.
  const stats = useMemo(() => {
    const items = data.items || [];
    let totalPaisePaid = 0;
    let trialCount = 0;
    let standardCount = 0;
    for (const it of items) {
      if (it.paidAmountPaise != null) {
        const n = Number(it.paidAmountPaise);
        if (Number.isFinite(n)) totalPaisePaid += n;
      }
      if (it.isTrial) trialCount += 1;
      else standardCount += 1;
    }
    return {
      pendingCount: data.total,
      totalPaisePaid,
      trialCount,
      standardCount
    };
  }, [data]);

  async function confirmApprove() {
    if (!approveTarget) return;
    setBusy(true);
    try {
      await internalAccounts.approve(approveTarget.tenantId);
      notifySuccess(
        'Account approved',
        `${approveTarget.legalName || approveTarget.slug} can now sign in.`
      );
      setApproveTarget(null);
      load();
    } catch (err) {
      notifyError(err, { title: 'Could not approve account' });
    } finally {
      setBusy(false);
    }
  }

  async function confirmReject() {
    if (!rejectTarget) return;
    const reason = rejectReason.trim();
    if (reason.length === 0) {
      notifyError(
        { code: 'VALIDATION_ERROR' },
        { title: 'Reason required', description: 'Enter a short reason for rejection.' }
      );
      return;
    }
    setBusy(true);
    try {
      await internalAccounts.reject(rejectTarget.tenantId, { reason });
      notifySuccess(
        'Account rejected',
        `${rejectTarget.legalName || rejectTarget.slug} marked rejected.`
      );
      setRejectTarget(null);
      setRejectReason('');
      load();
    } catch (err) {
      notifyError(err, { title: 'Could not reject account' });
    } finally {
      setBusy(false);
    }
  }

  const columns = useMemo(
    () => [
      {
        title: 'Company',
        key: 'company',
        width: 220,
        ellipsis: true,
        render: (_v, row) => <CompanyCell row={row} />
      },
      {
        title: 'GSTIN',
        dataIndex: 'gstin',
        key: 'gstin',
        width: 150,
        render: (v) =>
          v ? <code className="text-[11px] text-ink-700">{v}</code> : <span className="text-ink-400">—</span>
      },
      {
        title: 'Contact',
        key: 'contact',
        width: 220,
        ellipsis: true,
        render: (_v, row) => <ContactCell row={row} />
      },
      {
        title: 'Plan',
        key: 'plan',
        width: 130,
        render: (_v, row) => <PlanCell row={row} />
      },
      {
        title: 'Paid',
        key: 'paid',
        width: 110,
        align: 'right',
        render: (_v, row) => <MoneyText paise={row.paidAmountPaise} />
      },
      {
        title: 'Invoice',
        key: 'invoice',
        width: 110,
        render: (_v, row) =>
          row.invoiceNumber ? (
            <code className="text-[11px] text-ink-700">{row.invoiceNumber}</code>
          ) : (
            <span className="text-ink-400">—</span>
          )
      },
      {
        title: 'Payment',
        key: 'payment',
        width: 160,
        render: (_v, row) => <PaymentCell row={row} />
      },
      {
        title: 'Created',
        key: 'createdAt',
        width: 160,
        render: (_v, row) => (
          <span className="text-xs text-ink-600">{formatDateTime(row.createdAt)}</span>
        )
      },
      {
        title: 'Actions',
        key: 'actions',
        width: 240,
        fixed: 'right',
        render: (_v, row) => (
          <Space size={4} wrap={false}>
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/internal/accounts/${row.tenantId}`)}
            >
              View
            </Button>
            <Tooltip title={canApprove ? '' : 'You do not have accounts.approve'}>
              <Button
                size="small"
                type="primary"
                icon={<CheckCircleOutlined />}
                disabled={!canApprove}
                onClick={() => setApproveTarget(row)}
              >
                Approve
              </Button>
            </Tooltip>
            <Tooltip title={canReject ? '' : 'You do not have accounts.reject'}>
              <Button
                size="small"
                danger
                icon={<CloseCircleOutlined />}
                disabled={!canReject}
                onClick={() => {
                  setRejectReason('');
                  setRejectTarget(row);
                }}
              >
                Reject
              </Button>
            </Tooltip>
          </Space>
        )
      }
    ],
    [canApprove, canReject, navigate]
  );

  return (
    <div>
      <PageHeader
        title="Pending Accounts"
        subtitle="Review paid signups awaiting Glass-i approval before tenant master users can sign in."
        actions={
          <Space>
            <Tag color="orange" className="!text-sm !px-3 !py-1">
              {data.total} pending
            </Tag>
            <Button icon={<ReloadOutlined />} onClick={load} disabled={loading}>
              Refresh
            </Button>
          </Space>
        }
      />

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <StatCard
          label="Pending"
          value={stats.pendingCount}
          tone="amber"
          icon={<ClockCircleOutlined />}
          hint="Awaiting review"
        />
        <StatCard
          label="Total paid"
          value={paiseToINR(stats.totalPaisePaid)}
          tone="green"
          icon={<DollarOutlined />}
          hint="Across loaded rows"
        />
        <StatCard
          label="Trial signups"
          value={stats.trialCount}
          tone="gold"
          icon={<GiftOutlined />}
          hint={stats.trialCount === 1 ? 'Trial account' : 'Trial accounts'}
        />
        <StatCard
          label="Standard / Custom"
          value={stats.standardCount}
          tone="blue"
          icon={<FileTextOutlined />}
          hint={stats.standardCount === 1 ? 'Paid account' : 'Paid accounts'}
        />
      </div>

      {loading ? (
        <LoadingBlock tip="Loading pending accounts…" />
      ) : (
        <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden">
          <Table
            rowKey="tenantId"
            dataSource={data.items}
            columns={columns}
            size="middle"
            pagination={false}
            scroll={{ x: 1280 }}
            tableLayout="fixed"
            locale={{
              emptyText: error ? (
                <div className="py-12 text-center text-ink-500 text-sm">
                  Could not load pending accounts.
                </div>
              ) : (
                <div className="py-10">
                  <Empty
                    description={
                      <div className="text-ink-700">
                        <p className="text-sm font-semibold m-0">No pending accounts</p>
                        <p className="text-xs text-ink-500 m-0 mt-1">
                          New paid signups awaiting approval will appear here.
                        </p>
                      </div>
                    }
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                </div>
              )
            }}
          />
          {data.items.length > 0 ? (
            <div className="px-4 py-2 border-t border-surface-200 text-xs text-ink-500 flex items-center justify-between">
              <span>
                Showing {data.items.length} of {data.total} pending account
                {data.total === 1 ? '' : 's'}
              </span>
            </div>
          ) : null}
        </div>
      )}

      <Modal
        open={Boolean(approveTarget)}
        title="Approve account?"
        okText="Approve account"
        okButtonProps={{ type: 'primary', loading: busy }}
        cancelButtonProps={{ disabled: busy }}
        onOk={confirmApprove}
        onCancel={() => (busy ? null : setApproveTarget(null))}
        maskClosable={!busy}
      >
        <p className="m-0">
          This will activate{' '}
          <strong>{approveTarget && (approveTarget.legalName || approveTarget.slug)}</strong>
          {' '}and allow the master user to log in.
        </p>
        <p className="text-xs text-ink-500 mt-2 mb-0">
          You can later suspend the account if review issues are found.
        </p>
      </Modal>

      <Modal
        open={Boolean(rejectTarget)}
        title="Reject account?"
        okText="Reject account"
        okButtonProps={{
          danger: true,
          loading: busy,
          disabled: rejectReason.trim().length === 0
        }}
        cancelButtonProps={{ disabled: busy }}
        onOk={confirmReject}
        onCancel={() => {
          if (busy) return;
          setRejectTarget(null);
          setRejectReason('');
        }}
        maskClosable={!busy}
        destroyOnHidden
      >
        <p className="m-0 mb-2">
          Marking{' '}
          <strong>{rejectTarget && (rejectTarget.legalName || rejectTarget.slug)}</strong>
          {' '}as rejected will block sign-in and surface the reason in the audit trail.
        </p>
        <label className="text-xs uppercase tracking-wider text-ink-500 font-semibold">
          Reason (required)
        </label>
        <TextArea
          value={rejectReason}
          onChange={(e) => setRejectReason(e.target.value)}
          rows={3}
          maxLength={1000}
          placeholder="e.g. GSTIN does not match supplied address"
          disabled={busy}
        />
      </Modal>
    </div>
  );
}
